/**
 * Data Hub — the run, on the page.
 *
 * Everything after "pick a file" happens here, inline under the console header,
 * exactly as the reference does it. The dialog (`RunWizard`) only chooses the
 * file and hands over; it must never grow a stepper.
 *
 * Phase 1: File → Columns → Accounts → Categories → Metrics → Import → QC →
 * Heal → Promote, each stopping for the user. Phase 2 then runs automatically.
 *
 * Decisions are applied by rewriting the uploaded CSV rather than persisting a
 * mapping — the reference forgets account decisions between runs and ExecBridge
 * matches that deliberately.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import {
  AlertCircle,
  ArrowRight,
  ArrowUpCircle,
  Ban,
  CheckCircle2,
  Download,
  Loader2,
  LogOut,
  MinusCircle,
  ShieldCheck,
  Upload,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { createPortal } from "react-dom";

import { type MetricChoices } from "./AddCustomDialog";
import { ConfirmModalBody } from "./ConfirmModalBody";
import {
  DataHubStepper,
  PHASE2_STEPS,
  type Phase2Step,
  type StepId,
  type StepStatus,
} from "./DataHubStepper";
import { SyntheticBadge } from "./DatasetPill";
import { MatchStep } from "./MatchStep";
import { SearchableSelect, type OptionGroup } from "./SearchableSelect";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import Pagination from "../../Components/Pagination";
import { useAppDispatch } from "../../Store/Hooks";
import {
  acceptAllDataHubQcFixes,
  downloadDataHubRejectedRowsCsv,
  fetchDataHubAccountOptions,
  fetchDataHubImportTemplate,
  fetchDataHubMetricDefinitions,
  fetchDataHubQcConflicts,
  fetchDataHubQcIssues,
  fetchDataHubQcRunSummary,
  killDataHubLedgerRun,
  persistDataHubLedgerStep,
  rejectAllDataHubQcRows,
  resolveDataHubQcConflict,
  resolveDataHubQcRow,
  saveDataHubLedgerProgress,
} from "../../Store/Reducers/DataHubConsoleSlice";
import {
  describeError,
  type CategoryOption,
  type QcConflictGroup,
  type QcRunSummary,
  formatMeasure,
  type RunPhaseOutcome,
  type RunSession,
} from "../data/api";
import type { DatasetKind } from "../data/dataset";
import type { ResumeSnapshot } from "../data/resume";
import { attachRunSession, openRunSession } from "../data/runSession";
import type { PhaseKey } from "../data/types";
import { useActiveDataset } from "../hooks/useActiveDataset";
import {
  CATEGORY_ID_COLUMN,
  applyCategoryDecisions,
  categoryCandidates,
  fileIdsByCategory,
  proposeCategory,
} from "../lib/category-match";
import {
  DEFAULT_FORMATS,
  DEFAULT_SCALES,
  mergeChoices,
  seedFromFile,
} from "../lib/custom-metric-seed";
import {
  ACCOUNT_ID_COLUMN,
  applyDecisions,
  countDecisions,
  distinctValues,
  proposeMatch,
  stampAccountIds,
  type Candidate,
  type MatchProposal,
  metricIdSlug,
  resolveViaAliases,
  effectiveTarget,
} from "../lib/entity-match";
import {
  countColumnDecisions,
  duplicateTargets,
  isRequiredField,
  missingRequiredFor,
  proposeColumnMappingsFor,
  templateFields,
  type ColumnMapping,
  type ColumnTarget,
  type ImportTemplate,
} from "../lib/header-aliases";
import { customerFacingRunError } from "../lib/run-errors";
import { parseSheet } from "../lib/sheet-to-csv";
import { tableToCsv, type SheetTable } from "../lib/sheet-to-csv";

type Stage =
  | "columns"
  | "accounts"
  | "metrics"
  | "categories"
  | "import"
  | "qc"
  | "heal"
  | "promote"
  | "phase2"
  | "done";

const STAGE_ORDER: Stage[] = [
  "columns",
  "accounts",
  // Categories before metrics: a custom metric must be able to attach to a
  // category created in the same run.
  "categories",
  "metrics",
  "import",
  "qc",
  "heal",
  "promote",
  "phase2",
];

const PHASE_2: Phase2Step[] = PHASE2_STEPS.map(s => s.id);

/**
 * `dataset` is fixed when the file is picked and carried for the life of the
 * run — every artifact the run writes inherits it, so it must not be re-derived
 * from the pill mid-run.
 */
export type ActiveRun = {
  file: File;
  table: SheetTable;
  dataset: DatasetKind;
  /** Ledger row this wizard is writing to; absent when the ledger could not open. */
  ledgerRunId?: string;
  /** Restored from the ledger when this console was reopened. */
  resume?: ResumeSnapshot;
  /** True when the original file is not in storage and must be re-selected. */
  needsFile?: boolean;
};

type Props = {
  run: ActiveRun;
  /**
   * Called after the user confirms Leave run (progress already saved).
   * Parent should exit immediately — do not re-prompt.
   */
  onExit: () => void;
  /**
   * Immediate exit with no leave prompt (abandon after confirm, success Done,
   * Close when finished). Defaults to onExit.
   */
  onForceExit?: () => void;
  /** Lets the host know when Phase 2 has finished so leave prompts can stop. */
  onFinishedChange?: (finished: boolean) => void;
  /**
   * When set, Abandon / Leave render into this node (page header next to the
   * Import & Run Workflow title) instead of the console toolbar.
   */
  headerActionsContainer?: HTMLElement | null;
  /**
   * When set, Continue (and stage primary actions) render into this node —
   * pinned at the bottom of the host page (Sign Out level).
   * Pass with `footerPinned` so the console does not briefly render an
   * inline footer while the host ref mounts.
   */
  footerActionsContainer?: HTMLElement | null;
  /** Portal primary CTAs into `footerActionsContainer` (see above). */
  footerPinned?: boolean;
  /**
   * When set, the stepper renders into this node (below the Import & Run
   * Workflow title card) instead of inside the console body.
   */
  stepperContainer?: HTMLElement | null;
  /** Portal the stepper into `stepperContainer` (see above). */
  stepperPinned?: boolean;
  onRunComplete?: () => void;
  /** Fired on unmount / Leave so KPI Config can remount without losing the step. */
  onPersist?: (resume: ResumeSnapshot) => void;
};

/** One outcome's count, in its own unit. Null when the phase reported none. */
function measureLabel(o: RunPhaseOutcome): string | null {
  if (o.measure) return formatMeasure(o.measure);
  return o.count == null ? null : `${o.count.toLocaleString()} rows`;
}

export function RunConsole({
  run,
  onExit,
  onForceExit,
  onFinishedChange,
  headerActionsContainer = null,
  footerActionsContainer = null,
  footerPinned = false,
  stepperContainer = null,
  stepperPinned = false,
  onRunComplete,
  onPersist,
}: Props) {
  const dispatch = useAppDispatch();
  const exitNow = onForceExit ?? onExit;
  const { table: initialTable } = run;
  const [, selectDataset] = useActiveDataset();
  const [fileOverride, setFileOverride] = useState<{
    file: File;
    table: SheetTable;
  } | null>(null);
  const file = fileOverride?.file ?? run.file;
  const table = fileOverride?.table ?? initialTable;

  // Lock the viewing pill to this run's dataset for the life of the console so
  // API calls (import/QC/Phase 2) and the header never disagree mid-run.
  useEffect(() => {
    selectDataset(run.dataset);
  }, [run.dataset, selectDataset]);

  const resume = run.resume;
  const [stage, setStage] = useState<Stage>(
    () => (resume?.stage as Stage | undefined) ?? "columns"
  );

  useEffect(() => {
    onFinishedChange?.(stage === "done");
  }, [stage, onFinishedChange]);

  const [completed, setCompleted] = useState<Set<StepId>>(
    () =>
      new Set<StepId>((resume?.completed as StepId[] | undefined) ?? ["file"])
  );
  const [failed, setFailed] = useState<Set<StepId>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const [confirmAbandonOpen, setConfirmAbandonOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const [columns, setColumns] = useState<ColumnMapping[]>(
    () => resume?.columns ?? proposeColumnMappingsFor(table.headers, null)
  );
  const [columnPage, setColumnPage] = useState(1);
  const [columnPageSize, setColumnPageSize] = useState(15);
  /** The org's import template. Null means the built-in aliases are in use. */
  const [template, setTemplate] = useState<ImportTemplate | null>(null);

  const [accounts, setAccounts] = useState<Candidate[]>([]);
  const [metrics, setMetrics] = useState<Candidate[]>([]);
  /** Alternative metric ids (cleaned) → the metric they mean. */
  const [metricAliases, setMetricAliases] = useState<Record<string, string>>(
    {}
  );
  /** One entry per real category, by key — for the custom-metric form only. */
  const [categoryChoices, setCategoryChoices] = useState<CategoryOption[]>([]);
  /** Units, formats and scales in use, suggested by the custom-metric form. */
  const [metricChoices, setMetricChoices] = useState<MetricChoices>({
    units: [],
    formats: [],
    scales: [],
  });
  const [refsLoaded, setRefsLoaded] = useState(false);

  const [accountDecisions, setAccountDecisions] = useState<
    Record<string, string>
  >(() => resume?.accountDecisions ?? {});
  const [metricDecisions, setMetricDecisions] = useState<
    Record<string, string>
  >(() => resume?.metricDecisions ?? {});
  const [categoryDecisions, setCategoryDecisions] = useState<
    Record<string, string>
  >(() => resume?.categoryDecisions ?? {});

  const [session, setSession] = useState<RunSession | null>(null);
  const [outcomes, setOutcomes] = useState<RunPhaseOutcome[]>([]);

  /** Modal copy: plain language; raw server dumps stay out of the customer view. */
  const showRunError = (raw: string | null | undefined, phase?: string) => {
    setError(
      customerFacingRunError({
        phase,
        raw,
        variant: "full",
      })
    );
  };

  const [qcIssues, setQcIssues] = useState<any[] | null>(null);
  /** What the QC pass found. Shown on the QC step, before Heal asks for fixes. */
  const [qcSummary, setQcSummary] = useState<QcRunSummary | null>(null);
  /** Rows resolved in this Heal pass: id -> what was done. */
  const [healed, setHealed] = useState<Record<string, string>>({});
  /** Row currently being edited, and its pending value. */
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(
    null
  );
  const [healBusy, setHealBusy] = useState<string | null>(null);
  /** Heal narrowed to one problem type (probe id) and/or one metric. "" = all. */
  const [healProbe, setHealProbe] = useState("");
  const [healMetric, setHealMetric] = useState("");
  /**
   * Duplicate groups whose values disagree. Adjudicated before anything else in
   * Heal: until one row wins, the rest of the queue is arguing about a number
   * that may not survive.
   */
  const [conflicts, setConflicts] = useState<QcConflictGroup[] | null>(null);
  const [conflictBusy, setConflictBusy] = useState<string | null>(null);
  /** Groups settled in this Heal pass, recorded on the run step. */
  const [conflictsResolved, setConflictsResolved] = useState(0);
  /** Steps that could not be written to the run history. Never blocks a run. */
  const [auditWarnings, setAuditWarnings] = useState<string[]>([]);
  const [phase2Status, setPhase2Status] = useState<
    Partial<Record<Phase2Step, StepStatus>>
  >({});

  // Reference data for the three reconciliation steps.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [accs, defs, tpl] = await Promise.all([
          dispatch(fetchDataHubAccountOptions()).unwrap(),
          dispatch(fetchDataHubMetricDefinitions()).unwrap(),
          dispatch(fetchDataHubImportTemplate()).unwrap(),
        ]);
        if (!live) return;
        if (tpl && !tpl.from_fallback) {
          // Only re-propose for a *configured* template. The server's fallback
          // is the same vocabulary the client already used, so re-running would
          // discard any mapping the user had already changed for no gain.
          setTemplate(tpl);
          if (!run.resume?.columns) {
            setColumns(proposeColumnMappingsFor(table.headers, tpl));
          }
        }
        setAccounts(
          accs.map(a => ({ id: a.account_id, name: a.account_name }))
        );
        setMetrics(
          defs.metrics.map(m => ({
            id: m.metric_id,
            name: m.metric_name || m.metric_id,
          }))
        );
        setCategoryChoices(defs.categoryOptions ?? []);
        const aliasToId: Record<string, string> = {};
        defs.metrics.forEach(m =>
          (m.aliases ?? []).forEach(a => {
            aliasToId[metricIdSlug(a)] = m.metric_id;
          })
        );
        setMetricAliases(aliasToId);
        setMetricChoices({
          units: mergeChoices(
            defs.metrics.map(m => m.unit_of_measure),
            []
          ),
          formats: mergeChoices(
            defs.metrics.map(m => m.format),
            DEFAULT_FORMATS
          ),
          scales: mergeChoices(
            defs.metrics.map(m => m.scale),
            DEFAULT_SCALES
          ),
        });
      } catch (err: any) {
        if (live) showRunError(describeError(err));
      } finally {
        if (live) setRefsLoaded(true);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  /**
   * Which file header currently feeds a field.
   *
   * Takes a plain string rather than the built-in `CanonicalField` union: a
   * configured template may define fields the union does not know about, which
   * is the whole point of having one.
   */
  const headerFor = (field: string): string | null =>
    columns.find(c => c.target === field)?.header ?? null;

  const columnCounts = useMemo(() => countColumnDecisions(columns), [columns]);
  const columnPageCount = Math.max(
    1,
    Math.ceil(columns.length / columnPageSize)
  );
  useEffect(() => {
    setColumnPage(p => Math.min(p, columnPageCount));
  }, [columnPageCount]);
  const pagedColumns = useMemo(() => {
    const start = (columnPage - 1) * columnPageSize;
    return columns.slice(start, start + columnPageSize);
  }, [columns, columnPage, columnPageSize]);
  const columnBlockers = useMemo(() => {
    const out: string[] = [];
    const missing = missingRequiredFor(columns, template);
    if (missing.length) out.push(`No column maps to: ${missing.join(", ")}.`);
    const dupes = duplicateTargets(columns);
    if (dupes.length)
      out.push(`More than one column maps to: ${dupes.join(", ")}.`);
    return out;
  }, [columns, template]);

  const accountCol =
    headerFor("account_name") ?? headerFor("source_account_name");
  const metricCol = headerFor("metric_id");
  const categoryCol = headerFor("category");
  /** The file's own Category ID column, when it has one. */
  const categoryIdCol = headerFor("category_id");

  /** What the file says about one metric, to pre-fill "Add as custom". */
  const seedForMetric = (source: string) =>
    seedFromFile(source, table.rows, table.headers, metricCol, headerFor);

  const accountProposals = useMemo<MatchProposal[]>(
    () =>
      distinctValues(table.rows, accountCol).map(v =>
        proposeMatch(v.value, accounts, v.rowCount)
      ),
    [table.rows, accountCol, accounts]
  );
  const metricProposals = useMemo<MatchProposal[]>(
    () =>
      distinctValues(table.rows, metricCol).map(v =>
        resolveViaAliases(
          proposeMatch(v.value, metrics, v.rowCount, undefined, {
            matchIdSlug: true,
          }),
          metricAliases
        )
      ),
    [table.rows, metricCol, metrics, metricAliases]
  );
  // Exact only: the file's Category ID, then a known name. The old fuzzy
  // match mixed names and ids and overwrote the row with whichever won.
  const categoryProposals = useMemo<MatchProposal[]>(() => {
    const idsByText = fileIdsByCategory(table.rows, categoryCol, categoryIdCol);
    return distinctValues(table.rows, categoryCol).map(v =>
      proposeCategory(
        v.value,
        idsByText[v.value] ?? [],
        categoryChoices,
        v.rowCount
      )
    );
  }, [table.rows, categoryCol, categoryIdCol, categoryChoices]);

  /** Category values that need a person's choice before Continue. */
  const unresolvedCategoryConflicts = categoryProposals.filter(
    p => p.status === "ambiguous" && !effectiveTarget(p, categoryDecisions)
  ).length;

  /** The rows actually uploaded, after every step's decisions are applied. */
  const resolved = useMemo(() => {
    let rows = table.rows;
    let dropped = 0;
    const a = applyDecisions(
      rows,
      accountCol,
      accountProposals,
      accountDecisions,
      accounts
    );
    rows = a.rows;
    dropped += a.dropped;
    // Supply the resolved account UUID so the importer keys on identity rather
    // than falling back to a placeholder source id.
    rows = stampAccountIds(
      rows,
      accountCol,
      accountProposals,
      accountDecisions
    );
    // writeField "id": this column is metric_id, so write the identifier, not
    // the display name.
    const m = applyDecisions(
      rows,
      metricCol,
      metricProposals,
      metricDecisions,
      metrics,
      true,
      "id"
    );
    rows = m.rows;
    dropped += m.dropped;
    // Category is descriptive, not identity: an unmatched category never
    // excludes a row. A resolved one gets the catalog's name, and its id in a
    // separate column, instead of whichever string a fuzzy match picked.
    rows = applyCategoryDecisions(
      rows,
      categoryCol,
      categoryIdCol,
      categoryProposals,
      categoryDecisions,
      categoryChoices
    );
    return { rows, dropped };
  }, [
    table.rows,
    accountCol,
    accountProposals,
    accountDecisions,
    accounts,
    metricCol,
    metricProposals,
    metricDecisions,
    metrics,
    categoryCol,
    categoryIdCol,
    categoryProposals,
    categoryDecisions,
    categoryChoices,
  ]);

  const tick = (id: StepId) => setCompleted(prev => new Set(prev).add(id));
  const markFailed = (id: StepId) => setFailed(prev => new Set(prev).add(id));

  const goto = (next: Stage) => setStage(next);

  const advance = (from: StepId) => {
    tick(from);
    const i = STAGE_ORDER.indexOf(stage);
    if (i >= 0 && i < STAGE_ORDER.length - 1) goto(STAGE_ORDER[i + 1]);
  };

  const currentDecisions = (): Record<string, unknown> => ({
    column_mapping: columns,
    account_decisions: accountDecisions,
    category_decisions: categoryDecisions,
    metric_decisions: metricDecisions,
  });

  const snapshot = (): ResumeSnapshot => ({
    stage,
    completed: Array.from(completed),
    columns,
    accountDecisions,
    metricDecisions,
    categoryDecisions,
    pipelineRunId: session?.runId,
    batchId: session?.batchId?.(),
  });

  const snapRef = useRef({
    ledgerRunId: run.ledgerRunId,
    decisions: currentDecisions(),
    resume: snapshot(),
  });
  snapRef.current = {
    ledgerRunId: run.ledgerRunId,
    decisions: currentDecisions(),
    resume: snapshot(),
  };

  // Best-effort save when the console unmounts (KPI Config, tab change, Leave).
  useEffect(() => {
    return () => {
      const s = snapRef.current;
      void dispatch(
        saveDataHubLedgerProgress({
          runId: s.ledgerRunId,
          decisions: s.decisions,
        })
      );
      onPersist?.(s.resume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistAndAdvance = (
    from: StepId,
    decisions: Record<string, unknown>,
    close?: {
      outcome_counts?: Record<string, unknown>;
      artifact_evidence?: Record<string, unknown>;
    }
  ) => {
    // Persist in the background: a ledger hiccup must not freeze the wizard.
    void dispatch(
      persistDataHubLedgerStep({
        runId: run.ledgerRunId,
        step: from,
        decisions,
        close,
      })
    );
    advance(from);
  };

  const leave = () => {
    void dispatch(
      saveDataHubLedgerProgress({
        runId: run.ledgerRunId,
        decisions: currentDecisions(),
      })
    );
    onPersist?.(snapshot());
    exitNow();
  };

  const requestLeave = () => setConfirmLeaveOpen(true);

  const confirmLeave = () => {
    setConfirmLeaveOpen(false);
    leave();
  };

  const requestAbandon = () => setConfirmAbandonOpen(true);

  const confirmAbandon = async () => {
    setConfirmAbandonOpen(false);
    if (run.ledgerRunId) {
      try {
        await dispatch(
          killDataHubLedgerRun({
            runId: run.ledgerRunId,
            reason: "abandoned from console",
          })
        ).unwrap();
      } catch {
        /* still close the console */
      }
    }
    exitNow();
  };

  const reselectFile = async (picked: File | null) => {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      const parsed = await parseSheet(picked);
      setFileOverride({ file: picked, table: parsed });
      if (!run.resume?.columns) {
        setColumns(proposeColumnMappingsFor(parsed.headers, template));
      }
      await dispatch(
        saveDataHubLedgerProgress({
          runId: run.ledgerRunId,
          decisions: {
            source_filename: picked.name,
            sheet_name: parsed.sheet_name,
            row_count: parsed.rows.length,
          },
        })
      ).unwrap();
    } catch (err: any) {
      showRunError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // Restore the pipeline session after Import so QC / Heal / Promote continue.
  useEffect(() => {
    const pipelineRunId = resume?.pipelineRunId;
    if (!pipelineRunId) return;
    let live = true;
    attachRunSession(dispatch, pipelineRunId, file, resume?.batchId)
      .then(s => {
        if (live) setSession(s);
      })
      .catch(err => {
        if (live) showRunError(describeError(err));
      });
    return () => {
      live = false;
    };
    // Intentionally once per resume; file identity is fixed at hydrate time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume?.pipelineRunId]);

  const record = (o: RunPhaseOutcome) =>
    setOutcomes(prev => [...prev.filter(x => x.phase !== o.phase), o]);

  /** Import: open the session and stage the mapped rows. */
  const runImport = async () => {
    setBusy(true);
    setError(null);
    try {
      const upload = new File(
        [
          tableToCsv(
            {
              ...table,
              headers: [
                ...table.headers,
                ACCOUNT_ID_COLUMN,
                CATEGORY_ID_COLUMN,
              ],
              rows: resolved.rows,
            },
            [
              // The resolved id column replaces the file's own Category ID
              // column, which it already carries for unresolved rows.
              ...columns.map(c =>
                c.target === "category_id"
                  ? { ...c, target: "ignore" as const }
                  : c
              ),
              { header: ACCOUNT_ID_COLUMN, target: "account_id" },
              { header: CATEGORY_ID_COLUMN, target: "category_id" },
            ]
          ),
        ],
        `${file.name.replace(/\.[^.]+$/, "")}.csv`,
        { type: "text/csv" }
      );
      const s = await openRunSession(dispatch, upload, file.name);
      setSession(s);

      // Backfill the decision steps now that a run record exists. These made no
      // database change, but they are what a reviewer needs to understand the
      // import: 22 columns auto-matched, 1 changed, 6 accounts confirmed.
      const bookkeeping = await Promise.all([
        s.recordStep("file", {
          count: table.rows.length,
          extras: { sheet: table.sheet_name, columns: table.headers.length },
        }),
        s.recordStep("columns", {
          count: columnCounts.auto_matched + columnCounts.user_matched,
          extras: columnCounts,
        }),
        s.recordStep("accounts", {
          count: accountProposals.length,
          extras: countDecisions(accountProposals, accountDecisions),
        }),
        s.recordStep("categories", {
          count: categoryProposals.length,
          extras: countDecisions(categoryProposals, categoryDecisions),
        }),
        s.recordStep("metrics", {
          count: metricProposals.length,
          extras: countDecisions(metricProposals, metricDecisions),
        }),
      ]);
      setAuditWarnings(bookkeeping.filter(Boolean) as string[]);
      const outcome = await s.runPhase("import");
      record(outcome);
      if (outcome.status === "success") {
        await dispatch(
          persistDataHubLedgerStep({
            runId: run.ledgerRunId,
            step: "import",
            decisions: {},
            close: {
              artifact_evidence: {
                pipeline_run_id: s.runId,
                batch_id: s.batchId(),
              },
              outcome_counts: {
                count: outcome.count ?? table.rows.length,
              },
            },
          })
        ).unwrap();
        tick("import");
        goto("qc");
      } else {
        markFailed("import");
        showRunError(outcome.error ?? "Import failed.", "import");
      }
    } catch (err: any) {
      showRunError(describeError(err), "import");
      markFailed("import");
    } finally {
      setBusy(false);
    }
  };

  /** QC: run the probe engine, then load the review queue for Heal. */
  const runQc = async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await session.runPhase("cleanse");
      record(outcome);
      if (outcome.status === "success") {
        // Report what QC found and stay here. Sending the user straight to Heal
        // asked them to fix rows without telling them what was wrong or how
        // much of it there was.
        try {
          setQcSummary(
            await dispatch(fetchDataHubQcRunSummary(session.batchId())).unwrap()
          );
        } catch {
          setQcSummary(null);
        }
      } else {
        markFailed("qc");
        showRunError(outcome.error ?? "QC failed.", "cleanse");
      }
    } catch (err: any) {
      showRunError(describeError(err), "cleanse");
      markFailed("qc");
    } finally {
      setBusy(false);
    }
  };

  /** Leave QC for Heal, loading the review queue as we go. */
  const goToHeal = async () => {
    setBusy(true);
    setError(null);
    try {
      await dispatch(
        persistDataHubLedgerStep({
          runId: run.ledgerRunId,
          step: "qc",
          decisions: {},
          close: {
            outcome_counts: {
              rows_scanned: qcSummary?.rows_scanned ?? 0,
            },
          },
        })
      ).unwrap();
      tick("qc");
      setHealed({});
      setEditing(null);
      try {
        setQcIssues(
          await dispatch(
            fetchDataHubQcIssues({ limit: 500, batchId: session?.batchId() })
          ).unwrap()
        );
      } catch {
        setQcIssues([]);
      }
      setConflictsResolved(0);
      try {
        setConflicts(
          asConflicts(
            await dispatch(fetchDataHubQcConflicts(session?.batchId())).unwrap()
          )
        );
      } catch (err: any) {
        // Say so rather than blocking: Commit holds unadjudicated rows back on
        // its own, so a failed read here costs visibility, not correctness.
        setConflicts([]);
        showRunError(describeError(err));
      }
      goto("heal");
    } finally {
      setBusy(false);
    }
  };

  /** Promote: move the surviving staging rows into production. */
  const runPromote = async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await session.runPhase("commit");
      record(outcome);
      if (outcome.status === "success") {
        tick("promote");
        await dispatch(
          persistDataHubLedgerStep({
            runId: run.ledgerRunId,
            step: "promote",
            decisions: {},
            close: {
              outcome_counts: { count: outcome.count ?? null },
            },
          })
        ).unwrap();
        goto("phase2");
      } else {
        markFailed("promote");
        showRunError(outcome.error ?? "Promote failed.", "commit");
      }
    } catch (err: any) {
      showRunError(describeError(err), "commit");
      markFailed("promote");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Phase 2 runs straight through until a step fails. On failure the pipeline
   * stops: later steps are not called, the failure is recorded, and the success
   * dialog is not shown.
   */
  const runPhase2 = async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      let failedAt: Phase2Step | null = null;
      let failedDetail: string | null = null;

      for (let i = 0; i < PHASE_2.length; i++) {
        const step = PHASE_2[i];
        setPhase2Status(prev => ({ ...prev, [step]: "running" }));
        const outcome = await session.runPhase(step as PhaseKey);
        record(outcome);
        setPhase2Status(prev => ({
          ...prev,
          [step]: outcome.status as StepStatus,
        }));

        if (outcome.status !== "failed") continue;

        failedAt = step;
        failedDetail =
          outcome.error ??
          `${step} failed. Phase 2 stopped; later steps were not run.`;

        // Local-only: do not open later phases on the server after a hard stop.
        const remaining = PHASE_2.slice(i + 1);
        if (remaining.length > 0) {
          setPhase2Status(prev => {
            const next = { ...prev };
            for (const s of remaining) next[s] = "skipped";
            return next;
          });
          for (const s of remaining) {
            record({
              phase: s,
              status: "skipped",
              error: `prerequisite ${step} did not succeed`,
            });
          }
        }
        break;
      }

      if (failedAt) {
        showRunError(failedDetail, failedAt);
        onFinishedChange?.(false);
        return;
      }

      await session.close();
      goto("done");
      setSuccessOpen(true);
      onFinishedChange?.(true);
      onRunComplete?.();
    } catch (err: any) {
      showRunError(describeError(err));
      onFinishedChange?.(false);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Apply one row-level fix. `edit` clears the verdict so the next QC pass
   * re-evaluates the row; `ignore` accepts it as it stands. Both mirror
   * `qc_service.resolve`.
   */
  const healRow = async (
    rowId: string,
    action: "edit" | "ignore" | "accept" | "reject",
    value?: string
  ) => {
    setHealBusy(rowId);
    setError(null);
    try {
      await dispatch(
        resolveDataHubQcRow({
          row_id: rowId,
          action,
          value,
          probe_id: action === "accept" && healProbe ? healProbe : undefined,
        })
      ).unwrap();
      setHealed(prev => ({
        ...prev,
        [rowId]:
          action === "ignore"
            ? "ignored"
            : action === "reject"
              ? "rejected — will not be promoted"
              : action === "accept"
                ? "fix accepted"
                : `value set to ${value}`,
      }));
      setEditing(null);
    } catch (err: any) {
      showRunError(describeError(err));
    } finally {
      setHealBusy(null);
    }
  };

  /**
   * Settle one duplicate group by keeping a single row.
   *
   * Nothing here is inferable — the rows agree on account, metric and date and
   * disagree only on the number — so the choice is the user's and the group
   * leaves the pane the moment they make it.
   */
  const pickConflict = async (key: string, winnerRowId: string) => {
    setConflictBusy(key);
    setError(null);
    try {
      await dispatch(
        resolveDataHubQcConflict({ key, winner_row_id: winnerRowId })
      ).unwrap();
      setConflicts(prev => (prev ?? []).filter(g => g.key !== key));
      setConflictsResolved(n => n + 1);
    } catch (err: any) {
      showRunError(describeError(err));
    } finally {
      setConflictBusy(null);
    }
  };

  /** Apply every suggestion at once, then re-check so verdicts are current. */
  const acceptAll = async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const res = await dispatch(
        acceptAllDataHubQcFixes({
          batchId: session.batchId(),
          scope: {
            probeId: healProbe || null,
            metricId: healMetric || null,
          },
        })
      ).unwrap();
      if (res.rows_changed === 0) {
        showRunError("No rows had a suggested fix to accept.");
        return;
      }
      const outcome = await session.runPhase("cleanse");
      record(outcome);
      setHealed({});
      setQcIssues(
        await dispatch(
          fetchDataHubQcIssues({ limit: 500, batchId: session.batchId() })
        ).unwrap()
      );
      // A re-run recomputes dedupe. Decisions already made are preserved
      // server-side, but a new import can add groups — reload rather than
      // leaving a stale list gating the step.
      setConflicts(
        asConflicts(
          await dispatch(fetchDataHubQcConflicts(session.batchId())).unwrap()
        )
      );
    } catch (err: any) {
      showRunError(describeError(err), "cleanse");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Reject every row in the current problem-type / metric scope. Only offered
   * while a scope is chosen: rejecting the whole queue in one click would be
   * the bulk mistake this step exists to prevent.
   */
  const rejectScope = async () => {
    if (!session || !healProbe) return;
    setBusy(true);
    setError(null);
    try {
      await dispatch(
        rejectAllDataHubQcRows({
          batchId: session.batchId(),
          scope: {
            probeId: healProbe,
            metricId: healMetric || null,
          },
        })
      ).unwrap();
      setHealed({});
      setQcIssues(
        await dispatch(
          fetchDataHubQcIssues({ limit: 500, batchId: session.batchId() })
        ).unwrap()
      );
    } catch (err: any) {
      showRunError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // A run resumed straight into Heal skipped `goToHeal`, which is where the
  // queue is normally loaded, and sat on "Loading…" for good. Load it here
  // whenever Heal is showing without one.
  useEffect(() => {
    if (stage !== "heal" || !session) return;
    let live = true;
    if (qcIssues === null) {
      dispatch(fetchDataHubQcIssues({ limit: 500, batchId: session.batchId() }))
        .unwrap()
        .then(rows => live && setQcIssues(rows))
        .catch(() => live && setQcIssues([]));
    }
    if (conflicts === null) {
      dispatch(fetchDataHubQcConflicts(session.batchId()))
        .unwrap()
        .then(c => live && setConflicts(asConflicts(c)))
        .catch(() => live && setConflicts([]));
    }
    return () => {
      live = false;
    };
  }, [stage, session, qcIssues, conflicts]);

  // Promote's counts must describe the rows as they are now, not as the QC
  // pass left them: Heal changes verdicts, and a stale "0 ready" followed by
  // promoting nine rows is exactly what a reviewer must not see.
  useEffect(() => {
    if (stage !== "promote" || !session) return;
    let live = true;
    dispatch(fetchDataHubQcRunSummary(session.batchId()))
      .unwrap()
      .then(s => {
        if (live) setQcSummary(s);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [stage, session]);

  /** Re-run QC so edited rows get a fresh verdict before Promote. */
  const recheck = async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await session.runPhase("cleanse");
      record(outcome);
      setHealed({});
      setQcIssues(
        await dispatch(
          fetchDataHubQcIssues({ limit: 500, batchId: session.batchId() })
        ).unwrap()
      );
      // A re-run recomputes dedupe. Decisions already made are preserved
      // server-side, but a new import can add groups — reload rather than
      // leaving a stale list gating the step.
      setConflicts(
        asConflicts(
          await dispatch(fetchDataHubQcConflicts(session.batchId())).unwrap()
        )
      );
    } catch (err: any) {
      showRunError(describeError(err), "cleanse");
    } finally {
      setBusy(false);
    }
  };

  const setColumnTarget = (header: string, target: ColumnTarget) =>
    setColumns(prev =>
      prev.map(m => (m.header === header ? { ...m, target, auto: false } : m))
    );

  const currentStep: StepId | "phase2" | "done" =
    stage === "phase2"
      ? "phase2"
      : stage === "done"
        ? "done"
        : (stage as StepId);

  /**
   * What Promote will and will not move.
   *
   * Derived from the QC pass rather than asking the server again: these are the
   * same buckets Commit itself filters on, so showing anything else here would
   * be a second opinion that could disagree with what actually happens.
   */
  // Mirrors what Commit actually holds back (`UNPROMOTABLE_STATUSES`):
  // duplicates, unsettled conflicts, rows with a hard finding and rows a person
  // rejected. Rows with only warnings ARE promoted, so they are counted as
  // ready and called out below — this screen used to count them as held back,
  // then promote them.
  const qb = qcSummary?.buckets;
  const heldBack =
    (qb?.duplicate ?? 0) +
    (qb?.conflict ?? 0) +
    (qb?.rejected ?? 0) +
    (qb?.user_rejected ?? 0);
  const promotable = Math.max(0, (qcSummary?.rows_scanned ?? 0) - heldBack);
  const promotableWithFindings = qb?.open ?? 0;

  const openIssues = qcIssues?.length ?? 0;
  /** Duplicate groups still waiting on a decision. Gates the Heal step. */
  const unresolvedConflicts = conflicts?.length ?? 0;
  /** A finding belongs to the chosen problem type (or any, when none is). */
  const inHealScope = (f: any) => !healProbe || f.probe_id === healProbe;

  /** The queue narrowed to the chosen problem type and metric. */
  const scopedIssues = useMemo(
    () =>
      (qcIssues ?? []).filter(
        (r: any) =>
          (!healProbe ||
            ((r.findings ?? []) as any[]).some(
              f => f.probe_id === healProbe
            )) &&
          (!healMetric || r.metric_id === healMetric)
      ),
    [qcIssues, healProbe, healMetric]
  );

  /**
   * Problem types in the queue, with how many rows carry each — what the
   * reviewer picks from to work one kind of problem at a time.
   */
  const healProbeOptions = useMemo(() => {
    const rowsByProbe = new Map<string, number>();
    for (const r of qcIssues ?? []) {
      const seen: Record<string, boolean> = {};
      for (const f of (r.findings ?? []) as any[]) {
        if (!f.probe_id || seen[f.probe_id]) continue;
        seen[f.probe_id] = true;
        rowsByProbe.set(f.probe_id, (rowsByProbe.get(f.probe_id) ?? 0) + 1);
      }
    }
    const labelOf = (id: string) =>
      qcSummary?.by_probe.find(p => p.probe_id === id)?.label ??
      id.replace(/_/g, " ").toLowerCase();
    return Array.from(rowsByProbe.entries())
      .map(([id, rows]) => ({ id, label: labelOf(id), rows }))
      .sort((a, b) => b.rows - a.rows);
  }, [qcIssues, qcSummary]);

  /** Metrics present under the chosen problem type. */
  const healMetricOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of qcIssues ?? []) {
      if (
        healProbe &&
        !((r.findings ?? []) as any[]).some(f => f.probe_id === healProbe)
      )
        continue;
      const m = r.metric_id ?? "";
      if (m) counts.set(m, (counts.get(m) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([id, rows]) => ({ id, rows }))
      .sort((a, b) => b.rows - a.rows);
  }, [qcIssues, healProbe]);

  /** How many individual fixes the engine is offering in the current scope. */
  const suggestionCount = scopedIssues.reduce(
    (n: number, r: any) =>
      n +
      ((r.findings ?? []) as any[]).filter(
        f => inHealScope(f) && f.proposal?.proposed != null
      ).length,
    0
  );
  const healProbeLabel =
    healProbeOptions.find(o => o.id === healProbe)?.label ?? "";

  // Once every row of the chosen problem type is dealt with, it drops out of
  // the list. Fall back to "All problems" rather than leave an empty table
  // under a dropdown that no longer shows what it is filtering on.
  useEffect(() => {
    if (healProbe && !healProbeOptions.some(o => o.id === healProbe)) {
      setHealProbe("");
      setHealMetric("");
    } else if (
      healMetric &&
      !healMetricOptions.some(o => o.id === healMetric)
    ) {
      setHealMetric("");
    }
  }, [healProbe, healMetric, healProbeOptions, healMetricOptions]);

  /**
   * Target fields for one column, split by whether another column already
   * claims them. Two columns mapping to the same field is a blocker, so the
   * ones still free are the only ones normally worth reading.
   */
  const columnFieldGroups = (
    header: string,
    current: string
  ): OptionGroup[] => {
    const takenBy = new Map<string, string>();
    for (const c of columns) {
      if (c.target !== "ignore" && c.header !== header) {
        takenBy.set(c.target, c.header);
      }
    }
    const free: OptionGroup["options"] = [];
    const taken: OptionGroup["options"] = [];
    for (const f of templateFields(template)) {
      const label = `${f}${isRequiredField(f, template) ? " (required)" : ""}`;
      const owner = takenBy.get(f);
      if (owner && f !== current) {
        taken.push({ id: f, label: `${label} — ${owner}` });
      } else {
        free.push({ id: f, label });
      }
    }
    return [
      { heading: "Pending", options: free },
      { heading: "Already selected", options: taken },
    ];
  };

  const rowFixes = (r: any): any[] =>
    ((r.findings ?? []) as any[])
      .filter(inHealScope)
      .map(f => f.proposal)
      .filter((pr: any) => pr && pr.field && pr.proposed != null);

  /**
   * The Heal queue grouped by account.
   *
   * A flat list of 198 findings is unreadable — the same three format problems
   * repeat per account, and a reviewer has to re-establish context on every
   * row. Grouping lets them judge one account at a time, and accept it in one
   * click when the pattern is obviously right.
   *
   * Accounts are ordered by how much needs attention, so the worst is first.
   */
  const healGroups = useMemo(() => {
    const by = new Map<string, any[]>();
    for (const r of scopedIssues) {
      const key = r.account_name ?? r.source_account_name ?? "Unattributed";
      const list = by.get(key);
      if (list) list.push(r);
      else by.set(key, [r]);
    }
    // Array.from, not spread: CRA targets ES5, where spreading a Map iterator
    // does not compile.
    return Array.from(by.entries())
      .map(([account, rows]) => ({
        account,
        rows,
        fixes: rows.reduce((n: number, r: any) => n + rowFixes(r).length, 0),
      }))
      .sort((a, b) => b.rows.length - a.rows.length);
    // rowFixes reads the probe scope, which scopedIssues already tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedIssues]);

  /**
   * Accept every suggested fix for one account.
   *
   * Applied row by row through the same endpoint a single Accept uses, rather
   * than a new bulk endpoint — one code path to be correct, and a partial
   * failure leaves the rows it did fix fixed.
   */
  const acceptAccount = async (account: string, rows: any[]) => {
    if (!session) return;
    setHealBusy(`account:${account}`);
    setError(null);
    const applied: Record<string, string> = {};
    try {
      for (const r of rows) {
        if (rowFixes(r).length === 0 || healed[r.id]) continue;
        await dispatch(
          resolveDataHubQcRow({
            row_id: r.id,
            action: "accept",
            probe_id: healProbe || undefined,
          })
        ).unwrap();
        applied[r.id] = "fix accepted";
      }
      setHealed(prev => ({ ...prev, ...applied }));
    } catch (err: any) {
      // Keep whatever succeeded before the failure; saying nothing was applied
      // would send the user back over rows that are already fixed.
      setHealed(prev => ({ ...prev, ...applied }));
      showRunError(describeError(err));
    } finally {
      setHealBusy(null);
    }
  };

  /** Repeated columns the parser left out as true duplicates. */
  const droppedRepeats = table.repeated_columns_dropped ?? [];

  const fileNameMark = (
    <div className="flex min-w-0 max-w-full shrink-0 flex-nowrap items-center gap-2">
      <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
        File name
      </span>
      <span className="min-w-0 truncate rounded-full border border-gray-200 bg-white px-2.5 py-1 text-sm font-medium text-gray-800">
        {file.name}
      </span>
      <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-500">
        · {table.rows.length.toLocaleString()} rows · {table.headers.length}{" "}
        columns
        {resolved.dropped > 0 &&
          ` · ${resolved.dropped.toLocaleString()} will be skipped`}
      </span>
    </div>
  );

  return (
    <section className="datahub-workflow-scroll flex flex-col bg-white p-5">
      {(() => {
        const datasetMark = (
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Dataset for this run
            </span>
            {run.dataset === "synthetic" ? (
              <SyntheticBadge dataset={run.dataset} />
            ) : (
              <span className="rounded-full border border-gray-200 px-2.5 py-1 text-sm font-medium text-gray-800">
                Production
              </span>
            )}
          </div>
        );
        const leaveActions = (
          <div className="flex flex-wrap items-center gap-2">
            {datasetMark}
            {stage !== "done" && (
              <Button
                variant="outline-primary"
                onClick={requestAbandon}
                disabled={busy}
                className="!border-red-200 !text-red-600 hover:!bg-red-50 hover:!text-red-700"
                icon={<Ban className="h-3.5 w-3.5" />}
              >
                Abandon run
              </Button>
            )}
            <Button
              variant="outline-primary"
              onClick={stage === "done" ? exitNow : requestLeave}
              disabled={busy}
              icon={<X className="h-3.5 w-3.5" />}
            >
              {stage === "done" ? "Close" : "Leave run"}
            </Button>
          </div>
        );
        return headerActionsContainer
          ? createPortal(leaveActions, headerActionsContainer)
          : null;
      })()}

      {!headerActionsContainer && (
        <div className="mb-2 flex w-full shrink-0 flex-wrap items-center gap-2">
          <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Dataset for this run
              </span>
              {run.dataset === "synthetic" ? (
                <SyntheticBadge dataset={run.dataset} />
              ) : (
                <span className="rounded-full border border-gray-200 px-2.5 py-1 text-sm font-medium text-gray-800">
                  Production
                </span>
              )}
            </div>
            {stage !== "done" && (
              <Button
                variant="outline-primary"
                onClick={requestAbandon}
                disabled={busy}
                className="!border-red-200 !text-red-600 hover:!bg-red-50 hover:!text-red-700"
                icon={<Ban className="h-3.5 w-3.5" />}
              >
                Abandon run
              </Button>
            )}
            <Button
              variant="outline-primary"
              onClick={stage === "done" ? exitNow : requestLeave}
              disabled={busy}
              icon={<X className="h-3.5 w-3.5" />}
            >
              {stage === "done" ? "Close" : "Leave run"}
            </Button>
          </div>
        </div>
      )}

      <CommonModal
        isOpen={confirmLeaveOpen}
        toggle={() => setConfirmLeaveOpen(false)}
        title="Leave this run?"
        size="sm"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline-primary"
              onClick={() => setConfirmLeaveOpen(false)}
            >
              Stay
            </Button>
            <Button onClick={confirmLeave}>Leave run</Button>
          </div>
        }
      >
        <ConfirmModalBody
          icon={LogOut}
          tone="info"
          headline="Your progress is saved."
          detail="You can resume this run later from Pipeline Runs."
        />
      </CommonModal>

      <CommonModal
        isOpen={confirmAbandonOpen}
        toggle={() => setConfirmAbandonOpen(false)}
        title="Abandon this run?"
        size="sm"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline-primary"
              onClick={() => setConfirmAbandonOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void confirmAbandon()}
              className="!bg-red-600 hover:!bg-red-700"
            >
              Abandon run
            </Button>
          </div>
        }
      >
        <ConfirmModalBody
          icon={Ban}
          tone="danger"
          headline="This run cannot be resumed."
          detail="Use Leave run instead if you want to come back later."
        />
      </CommonModal>

      <CommonModal
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        title="Upload successful"
        size="sm"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setSuccessOpen(false);
                exitNow();
              }}
            >
              Done
            </Button>
          </div>
        }
      >
        <ConfirmModalBody
          icon={CheckCircle2}
          tone="success"
          headline="Upload finished successfully."
          detail="Phase 1 and Phase 2 completed. Your metrics data is ready."
        />
      </CommonModal>

      <CommonModal
        isOpen={Boolean(error)}
        toggle={() => setError(null)}
        title="Something went wrong"
        size="sm"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setError(null)}>OK</Button>
          </div>
        }
      >
        <ConfirmModalBody
          icon={AlertCircle}
          tone="danger"
          headline="The process could not be completed."
          detail={error}
        />
      </CommonModal>

      {(() => {
        const stepper = (
          <DataHubStepper
            current={currentStep}
            completed={completed}
            failed={failed}
            phase2StepStatus={phase2Status}
          />
        );
        if (stepperPinned) {
          return stepperContainer
            ? createPortal(stepper, stepperContainer)
            : null;
        }
        return <div className="mt-3 shrink-0">{stepper}</div>;
      })()}

      <div className="flex flex-col gap-2 pr-0.5">
        {run.needsFile && !fileOverride && (
          <div className="shrink-0 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            <p className="m-0 font-medium">
              The original file is not in storage. Choose it again to continue
              this run.
            </p>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv"
              disabled={busy}
              onChange={e => reselectFile(e.target.files?.[0] ?? null)}
            />
          </div>
        )}

        {stage === "columns" && (
          <div className="flex flex-col gap-3">
            <div className="flex shrink-0 flex-wrap items-start gap-3">
              <div className="min-w-[16rem] flex-1">
                <p className="m-0 text-base font-semibold text-gray-900">
                  Map columns
                </p>
                <p className="m-0 mt-0.5 text-sm leading-relaxed text-gray-500">
                  Auto-matches are pre-filled. Change any that are wrong, and
                  ignore columns you do not want imported.
                  {template && " Using this organization's import template."}
                </p>
                {droppedRepeats.length > 0 && (
                  <p className="m-0 mt-1 text-xs text-gray-500">
                    Your file repeats{" "}
                    {droppedRepeats.length === 1
                      ? "a column"
                      : `${droppedRepeats.length} columns`}{" "}
                    ({droppedRepeats.join(", ")}) with nothing new in the second
                    copy, so{" "}
                    {droppedRepeats.length === 1 ? "it was" : "they were"} left
                    out.
                  </p>
                )}
              </div>
              {fileNameMark}
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <span className="rounded-md bg-gray-100 px-2.5 py-1 text-sm text-gray-700">
                {columnCounts.auto_matched} auto-matched
              </span>
              <span className="rounded-md bg-gray-100 px-2.5 py-1 text-sm text-gray-700">
                {columnCounts.user_matched} user-matched
              </span>
              <span className="rounded-md bg-gray-100 px-2.5 py-1 text-sm text-gray-700">
                {columnCounts.ignored} ignored
              </span>
            </div>

            {columnBlockers.length > 0 && (
              <div className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                {columnBlockers.map(b => (
                  <p key={b} className="m-0">
                    {b}
                  </p>
                ))}
              </div>
            )}

            {/* Full table in page flow — page scrolls; paginated (25 default). */}
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Column in your file
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Sample
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Imports as
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedColumns.map(m => (
                    <tr key={m.header} className="border-t border-gray-100">
                      <td className="max-w-[220px] truncate px-3 py-2.5 font-semibold text-gray-900">
                        {m.header}
                      </td>
                      <td className="max-w-[200px] truncate px-3 py-2.5 text-gray-600">
                        {table.rows[0]?.[m.header] || "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <SearchableSelect
                            ariaLabel={`Imports as, for ${m.header}`}
                            value={m.target}
                            onChange={v =>
                              setColumnTarget(m.header, v as ColumnTarget)
                            }
                            leadingOption={{
                              id: "ignore",
                              label: "— Do not import —",
                            }}
                            groups={columnFieldGroups(m.header, m.target)}
                            searchPlaceholder="Search fields…"
                            className="w-[260px]"
                          />
                          {m.target !== "ignore" && !m.auto && (
                            <span className="text-xs font-medium text-blue-600">
                              changed
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              mode="static"
              currentPage={columnPage}
              pageSize={columnPageSize}
              dataLength={columns.length}
              onPageChange={setColumnPage}
              onPageSizeChange={setColumnPageSize}
              pageSizeOptions={[15, 25, 50, 100]}
              className="mt-1"
            />
          </div>
        )}

        {stage !== "columns" && (
          <div className="flex flex-col">
            <div className="space-y-3">
              {stage === "accounts" &&
                (refsLoaded ? (
                  <MatchStep
                    title="Confirm accounts"
                    hint="Each account named in your file, matched against this organization. Decisions apply to this run only."
                    sourceLabel="Account in your file"
                    proposals={accountProposals}
                    candidates={accounts}
                    decisions={accountDecisions}
                    counts={countDecisions(accountProposals, accountDecisions)}
                    onDecide={(source, target) =>
                      setAccountDecisions(prev => ({
                        ...prev,
                        [source]: target,
                      }))
                    }
                    emptyNote="No account column is mapped, so there is nothing to confirm."
                    headerTrailing={fileNameMark}
                  />
                ) : (
                  <Loading />
                ))}

              {stage === "metrics" &&
                (refsLoaded ? (
                  <MatchStep
                    title="Confirm metrics"
                    hint="Each metric in your file, matched against the metric definitions."
                    sourceLabel="Metric in your file"
                    proposals={metricProposals}
                    candidates={metrics}
                    decisions={metricDecisions}
                    counts={countDecisions(metricProposals, metricDecisions)}
                    onDecide={(source, target) =>
                      setMetricDecisions(prev => ({
                        ...prev,
                        [source]: target,
                      }))
                    }
                    emptyNote="No metric column is mapped, so there is nothing to confirm."
                    customKind="metric"
                    categoryOptions={categoryChoices}
                    customSeedFor={seedForMetric}
                    customChoices={metricChoices}
                    headerTrailing={fileNameMark}
                    onCustomCreated={created =>
                      setMetrics(prev =>
                        prev.some(m => m.id === created.id)
                          ? prev
                          : [...prev, created]
                      )
                    }
                  />
                ) : (
                  <Loading />
                ))}

              {stage === "categories" &&
                (refsLoaded ? (
                  <MatchStep
                    title="Confirm categories"
                    hint="Categories in your file, matched to the scoring rubric. Anything with no counterpart is kept as a new category — the row still imports."
                    keepUnmatched
                    sourceLabel="Category in your file"
                    proposals={categoryProposals}
                    candidates={categoryCandidates(categoryChoices)}
                    decisions={categoryDecisions}
                    counts={countDecisions(
                      categoryProposals,
                      categoryDecisions
                    )}
                    onDecide={(source, target) =>
                      setCategoryDecisions(prev => ({
                        ...prev,
                        [source]: target,
                      }))
                    }
                    emptyNote="No category column is mapped, so there is nothing to confirm."
                    customKind="category"
                    headerTrailing={fileNameMark}
                    onCustomCreated={created =>
                      setCategoryChoices(prev =>
                        prev.some(c => c.key === created.id)
                          ? prev
                          : [...prev, { key: created.id, label: created.name }]
                      )
                    }
                  />
                ) : (
                  <Loading />
                ))}

              {stage === "import" && (
                <Panel
                  title="Import"
                  subtitle={
                    <>
                      Stage{" "}
                      <strong>{resolved.rows.length.toLocaleString()}</strong>{" "}
                      rows into staging.
                      {resolved.dropped > 0 && (
                        <>
                          {" "}
                          <strong>
                            {resolved.dropped.toLocaleString()}
                          </strong>{" "}
                          rows are excluded because their account, metric or
                          category was skipped.
                        </>
                      )}
                    </>
                  }
                  headerTrailing={fileNameMark}
                />
              )}

              {stage === "qc" && !qcSummary && (
                <Panel
                  title="QC"
                  subtitle="Run the quality probes over the staged rows. What they find is reported here before anything is changed."
                  headerTrailing={fileNameMark}
                />
              )}

              {stage === "qc" && qcSummary && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="min-w-[16rem] flex-1">
                      <p className="m-0 text-base font-semibold text-gray-900">
                        What QC found
                      </p>
                      <p className="m-0 text-sm text-gray-500">
                        {qcSummary.rows_scanned.toLocaleString()} row
                        {qcSummary.rows_scanned === 1 ? "" : "s"} checked.
                        Nothing has been changed beyond the automatic fixes
                        below.
                      </p>
                    </div>
                    {fileNameMark}
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[
                      {
                        label: "Clean",
                        value: qcSummary.buckets.clean,
                        tone: "text-green-700",
                      },
                      {
                        label: "Auto-fixed",
                        value: qcSummary.buckets.fixed,
                        tone: "text-blue-700",
                      },
                      {
                        label: "Open issues",
                        value: qcSummary.buckets.open,
                        tone: "text-amber-700",
                      },
                      {
                        label: "Rejected",
                        value: qcSummary.buckets.rejected,
                        tone: "text-red-700",
                      },
                    ].map(b => (
                      <div
                        key={b.label}
                        className="rounded-md border border-gray-200 px-3 py-2"
                      >
                        <p className="m-0 text-xs font-medium uppercase tracking-wide text-gray-500">
                          {b.label}
                        </p>
                        <p className={`m-0 text-xl font-semibold ${b.tone}`}>
                          {b.value.toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>

                  {qcSummary.dedupe.auto_deduped > 0 && (
                    <p className="m-0 text-xs text-gray-600">
                      {qcSummary.dedupe.auto_deduped.toLocaleString()} duplicate
                      row
                      {qcSummary.dedupe.auto_deduped === 1 ? "" : "s"} merged
                      automatically
                      {qcSummary.dedupe.conflicts > 0 &&
                        `, ${qcSummary.dedupe.conflicts} left as conflicts for review`}
                      .
                    </p>
                  )}

                  {qcSummary.by_probe.length === 0 ? (
                    <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                      No probe raised anything. Nothing to heal.
                    </div>
                  ) : (
                    <div className="max-h-[260px] overflow-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse text-sm">
                        <thead className="sticky top-0 bg-gray-50">
                          <tr>
                            <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Probe
                            </th>
                            <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Severity
                            </th>
                            <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Rows
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {qcSummary.by_probe.map(pr => (
                            <tr
                              key={pr.probe_id}
                              className="border-t border-gray-100"
                            >
                              <td className="px-3 py-2.5">
                                <span className="font-semibold text-gray-900">
                                  {pr.label}
                                </span>{" "}
                                <span className="font-mono text-xs text-gray-400">
                                  {pr.probe_id}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <span
                                  className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                                    pr.severity === "hard"
                                      ? "bg-red-100 text-red-800"
                                      : pr.severity === "auto-fix"
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-amber-100 text-amber-900"
                                  }`}
                                >
                                  {pr.severity ?? "warn"}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-left tabular-nums text-gray-700">
                                {pr.count.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <p className="m-0 text-xs text-gray-500">
                    A <strong>hard</strong> finding stops a row reaching
                    production; a <strong>warn</strong> does not. Continue to
                    Heal to act on them.
                  </p>
                </div>
              )}

              {stage === "heal" && (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-4">
                    <div className="min-w-0 flex-1 space-y-1">
                      <h2 className="m-0 text-lg font-bold tracking-tight text-gray-900">
                        Heal
                      </h2>
                      <p className="m-0 max-w-2xl text-sm leading-relaxed text-gray-500">
                        Rows QC flagged. Pick a problem type to review one kind
                        at a time. Rows with a hard finding, and rows you
                        reject, are not promoted.
                      </p>
                    </div>
                    {/* Same pattern as Dataset → Abandon / Leave: mark left of CTAs. */}
                    <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                      {fileNameMark}
                      {openIssues > 0 && (
                        <Button
                          variant="secondary"
                          onClick={() =>
                            dispatch(
                              downloadDataHubRejectedRowsCsv(session?.batchId())
                            )
                              .unwrap()
                              .catch(err => showRunError(describeError(err)))
                          }
                          icon={<Download className="h-3.5 w-3.5" />}
                        >
                          Download flagged rows
                        </Button>
                      )}
                      {suggestionCount > 0 && (
                        <Button onClick={acceptAll} loading={busy}>
                          {healProbe
                            ? `Accept ${suggestionCount} fix${
                                suggestionCount === 1 ? "" : "es"
                              }: ${healProbeLabel}`
                            : `Accept all ${suggestionCount} fix${
                                suggestionCount === 1 ? "" : "es"
                              }`}
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Step one of Heal. Duplicates that disagree are settled before the
              probe queue, because promoting is blocked until they are and
              healing a row that loses the group is wasted work. */}
                  {conflicts === null ? (
                    <Loading />
                  ) : conflicts.length > 0 ? (
                    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/80 p-4">
                      <div className="space-y-1">
                        <p className="m-0 text-sm font-semibold text-amber-950">
                          Duplicate values to resolve ({conflicts.length})
                        </p>
                        <p className="m-0 text-sm leading-relaxed text-amber-800/90">
                          These rows share an account, metric and date but
                          disagree on the value. Pick the one to keep — the rest
                          are discarded. Promote stays blocked until every group
                          is settled.
                        </p>
                      </div>
                      <div className="max-h-[320px] space-y-3 overflow-auto">
                        {conflicts.map(group => (
                          <div
                            key={group.key}
                            className="rounded-lg border border-amber-100 bg-white p-3 shadow-sm"
                          >
                            <div className="mb-2.5 text-sm text-gray-700">
                              <span className="font-semibold text-gray-900">
                                {group.account_name ?? "—"}
                              </span>
                              <span className="text-gray-400"> · </span>
                              <span className="text-gray-600">
                                {group.metric_name ?? group.metric_id ?? "—"}
                              </span>
                              <span className="text-gray-400"> · </span>
                              <span className="tabular-nums text-gray-500">
                                {group.measurement_date ?? "—"}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {group.candidates.map(c => (
                                <Button
                                  key={c.row_id}
                                  variant="outline-primary"
                                  className="h-8 px-3 text-xs"
                                  disabled={conflictBusy === group.key}
                                  onClick={() =>
                                    pickConflict(group.key, c.row_id)
                                  }
                                  title={
                                    c.source_system
                                      ? `from ${c.source_system}`
                                      : undefined
                                  }
                                >
                                  Keep {c.value ?? "—"}
                                </Button>
                              ))}
                              {conflictBusy === group.key && (
                                <Loader2 className="mt-1.5 h-4 w-4 animate-spin text-amber-700" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {qcIssues === null ? (
                    <Loading />
                  ) : openIssues === 0 ? (
                    unresolvedConflicts === 0 ? (
                      <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                        Nothing to heal — no rows were flagged.
                      </div>
                    ) : (
                      // "No rows were flagged" beside an amber duplicate pane reads as
                      // a contradiction. Say what is actually outstanding.
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        No probe findings — only the duplicate values above are
                        left to resolve.
                      </div>
                    )
                  ) : (
                    <>
                      {/* Work one kind of problem at a time: judge the scale
                        fixes, accept them together, then move on. */}
                      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                        <label className="flex min-w-[220px] flex-col gap-1 text-xs font-medium text-gray-600">
                          Problem type
                          <select
                            aria-label="Problem type"
                            value={healProbe}
                            onChange={e => {
                              setHealProbe(e.target.value);
                              setHealMetric("");
                            }}
                            className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800"
                          >
                            <option value="">
                              All problems ({openIssues.toLocaleString()} rows)
                            </option>
                            {healProbeOptions.map(o => (
                              <option key={o.id} value={o.id}>
                                {o.label} ({o.rows.toLocaleString()})
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="flex min-w-[200px] flex-col gap-1 text-xs font-medium text-gray-600">
                          Metric
                          <select
                            aria-label="Metric"
                            value={healMetric}
                            onChange={e => setHealMetric(e.target.value)}
                            className="h-9 rounded-md border border-gray-300 bg-white px-2 font-mono text-sm text-gray-800"
                          >
                            <option value="">All metrics</option>
                            {healMetricOptions.map(o => (
                              <option key={o.id} value={o.id}>
                                {o.id} ({o.rows})
                              </option>
                            ))}
                          </select>
                        </label>
                        <span className="pb-2 text-xs text-gray-500">
                          Showing {scopedIssues.length.toLocaleString()} of{" "}
                          {openIssues.toLocaleString()} flagged rows
                        </span>
                        {healProbe && scopedIssues.length > 0 && (
                          <Button
                            variant="secondary"
                            className="ml-auto h-9 px-3 text-xs text-red-700"
                            disabled={busy}
                            onClick={rejectScope}
                          >
                            Reject these {scopedIssues.length.toLocaleString()}{" "}
                            row{scopedIssues.length === 1 ? "" : "s"}
                          </Button>
                        )}
                      </div>
                      <div className="max-h-[440px] overflow-auto rounded-lg border border-gray-200">
                        <table className="w-full border-collapse text-sm">
                          <thead className="sticky top-0 z-10 bg-gray-50">
                            <tr className="border-b border-gray-200">
                              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Metric
                              </th>
                              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Value
                              </th>
                              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Finding
                              </th>
                              <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Fix
                              </th>
                            </tr>
                          </thead>
                          {healGroups.map(group => (
                            <tbody key={group.account}>
                              {/* One band per account: the reviewer reads a single
                        account's metrics together, and can accept the lot when
                        the pattern is obviously right. */}
                              <tr className="border-t border-gray-200 bg-slate-50">
                                <td colSpan={4} className="px-3 py-2.5">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <span className="text-sm font-semibold text-gray-900">
                                        {group.account}
                                      </span>
                                      <span className="ml-2 text-xs text-gray-500">
                                        {group.rows.length} row
                                        {group.rows.length === 1 ? "" : "s"}
                                        {group.fixes > 0 &&
                                          ` · ${group.fixes} suggested`}
                                      </span>
                                    </div>
                                    {group.fixes > 0 &&
                                      group.rows.some(
                                        (r: any) => !healed[r.id]
                                      ) && (
                                        <Button
                                          variant="secondary"
                                          className="h-8 px-3 text-xs"
                                          loading={
                                            healBusy ===
                                            `account:${group.account}`
                                          }
                                          disabled={busy}
                                          onClick={() =>
                                            acceptAccount(
                                              group.account,
                                              group.rows
                                            )
                                          }
                                        >
                                          Accept all fixes for this account
                                        </Button>
                                      )}
                                  </div>
                                </td>
                              </tr>
                              {group.rows.map((r: any, i: number) => (
                                <tr
                                  key={r.id ?? i}
                                  className="border-t border-gray-100 align-top"
                                >
                                  {/* Account lives in the group band above, not on every
                          row — repeating it was most of the visual noise. */}
                                  <td className="max-w-[200px] truncate px-3 py-3 pl-5 font-mono text-xs text-gray-600">
                                    {r.metric_id ?? r.metric_name ?? "—"}
                                  </td>
                                  <td className="px-3 py-3 text-left tabular-nums text-sm text-gray-800">
                                    {editing && editing.id === r.id ? (
                                      <input
                                        autoFocus
                                        value={editing.value}
                                        onChange={(
                                          e: React.ChangeEvent<HTMLInputElement>
                                        ) =>
                                          setEditing({
                                            id: r.id,
                                            value: e.target.value,
                                          })
                                        }
                                        className="h-8 w-28 rounded-md border border-gray-300 px-2.5 text-left text-sm"
                                      />
                                    ) : (
                                      (r.value ?? "—")
                                    )}
                                  </td>
                                  {/* Findings come back at `findings`, not nested under `qc`
                          — see qc_service.issues. */}
                                  <td className="px-3 py-3 text-sm text-gray-700">
                                    {(r.findings ?? []).length === 0 ? (
                                      "—"
                                    ) : (
                                      <div className="space-y-1.5">
                                        {(r.findings ?? []).map(
                                          (f: any, j: number) => (
                                            <div
                                              key={j}
                                              className="flex flex-wrap items-start gap-2"
                                            >
                                              <span
                                                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                                  f.severity === "hard"
                                                    ? "bg-red-100 text-red-800"
                                                    : "bg-amber-100 text-amber-900"
                                                }`}
                                              >
                                                {f.severity}
                                              </span>
                                              <span className="min-w-0 leading-snug text-gray-700">
                                                {f.message}
                                              </span>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-3">
                                    {(() => {
                                      const suggestions = (
                                        (r.findings ?? []) as any[]
                                      )
                                        .filter(inHealScope)
                                        .map(f => f.proposal)
                                        .filter(
                                          (pr: any) =>
                                            pr &&
                                            pr.field &&
                                            pr.proposed != null
                                        );
                                      if (
                                        healed[r.id] ||
                                        suggestions.length === 0
                                      )
                                        return null;
                                      return (
                                        <div className="mb-2 space-y-1">
                                          {suggestions.map(
                                            (pr: any, k: number) => (
                                              <div
                                                key={k}
                                                className="text-xs leading-snug text-gray-600"
                                              >
                                                <span className="font-medium text-gray-500">
                                                  {pr.field}:
                                                </span>{" "}
                                                <span className="line-through text-gray-400">
                                                  {pr.current || "(missing)"}
                                                </span>{" "}
                                                <span className="font-medium text-green-700">
                                                  → {pr.proposed}
                                                </span>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      );
                                    })()}
                                    {healed[r.id] ? (
                                      <span className="text-xs font-medium text-green-700">
                                        {healed[r.id]}
                                      </span>
                                    ) : editing && editing.id === r.id ? (
                                      <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                          variant="primary"
                                          className="h-8 px-3 text-xs"
                                          disabled={healBusy === r.id}
                                          onClick={() =>
                                            healRow(r.id, "edit", editing.value)
                                          }
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          variant="secondary"
                                          className="h-8 px-3 text-xs"
                                          onClick={() => setEditing(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex flex-wrap items-center gap-2">
                                        {((r.findings ?? []) as any[]).some(
                                          f =>
                                            inHealScope(f) &&
                                            f.proposal?.proposed != null
                                        ) && (
                                          <Button
                                            variant="primary"
                                            className="h-8 px-3 text-xs"
                                            disabled={healBusy === r.id}
                                            onClick={() =>
                                              healRow(r.id, "accept")
                                            }
                                          >
                                            Accept fix
                                          </Button>
                                        )}
                                        <Button
                                          variant="secondary"
                                          className="h-8 px-3 text-xs"
                                          onClick={() =>
                                            setEditing({
                                              id: r.id,
                                              value: String(r.value ?? ""),
                                            })
                                          }
                                        >
                                          Edit value
                                        </Button>
                                        <Button
                                          variant="clear"
                                          className="h-8 px-2 text-xs text-gray-500 hover:text-gray-800"
                                          disabled={healBusy === r.id}
                                          onClick={() =>
                                            healRow(r.id, "ignore")
                                          }
                                        >
                                          Ignore
                                        </Button>
                                        <Button
                                          variant="clear"
                                          className="h-8 px-2 text-xs text-red-600 hover:text-red-800"
                                          disabled={healBusy === r.id}
                                          onClick={() =>
                                            healRow(r.id, "reject")
                                          }
                                        >
                                          Reject
                                        </Button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          ))}
                        </table>
                      </div>
                    </>
                  )}
                  <div className="flex flex-wrap items-start justify-between gap-3 border-t border-gray-100 pt-4">
                    <p className="m-0 max-w-3xl text-xs leading-relaxed text-gray-500">
                      <strong className="font-semibold text-gray-700">
                        Reject
                      </strong>{" "}
                      keeps a row out of production, as does any open{" "}
                      <strong className="font-semibold text-gray-700">
                        hard
                      </strong>{" "}
                      finding; rows with only warnings are promoted as they
                      stand.{" "}
                      <strong className="font-semibold text-gray-700">
                        Edit value
                      </strong>{" "}
                      clears the verdict so the next QC pass re-checks the row;{" "}
                      <strong className="font-semibold text-gray-700">
                        Ignore
                      </strong>{" "}
                      accepts it as it stands.
                    </p>
                    {Object.keys(healed).length > 0 && (
                      <Button
                        variant="outline-primary"
                        onClick={recheck}
                        loading={busy}
                      >
                        Re-check {Object.keys(healed).length} fixed row
                        {Object.keys(healed).length === 1 ? "" : "s"}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {stage === "promote" && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="min-w-[16rem] flex-1">
                      <p className="m-0 text-base font-semibold text-gray-900">
                        Promote
                      </p>
                      <p className="m-0 text-sm text-gray-500">
                        Move the staged rows that passed QC into{" "}
                        {run.dataset === "synthetic" ? (
                          <span className="font-medium text-violet-700">
                            the synthetic dataset
                          </span>
                        ) : (
                          "production"
                        )}
                        . Rows that failed stay in staging for review rather
                        than being deleted.
                      </p>
                    </div>
                    {fileNameMark}
                  </div>

                  {/* Promotion happens here and only here. Heal resolves rows; it does
              not commit them, so there is exactly one place in the console
              where data reaches production and one number to check first. */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-md border border-gray-200 px-3 py-2">
                      <p className="m-0 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        Ready to promote
                      </p>
                      <p className="m-0 text-xl font-semibold text-green-700">
                        {promotable.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-md border border-gray-200 px-3 py-2">
                      <p className="m-0 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        Held back
                      </p>
                      <p className="m-0 text-xl font-semibold text-amber-700">
                        {heldBack.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {heldBack > 0 && (
                    <p className="m-0 text-xs text-gray-500">
                      Held back: duplicates, unsettled conflicts, rows with a
                      hard finding and rows you rejected. They stay in staging
                      and can be healed and promoted in a later run. Nothing is
                      discarded.
                    </p>
                  )}
                  {promotableWithFindings > 0 && (
                    <p className="m-0 text-xs text-amber-700">
                      {promotableWithFindings.toLocaleString()} of the rows
                      ready to promote still carry warnings. Go back to Heal to
                      fix or reject them, or promote them as they are.
                    </p>
                  )}

                  {busy && (
                    <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Promoting {promotable.toLocaleString()} row
                      {promotable === 1 ? "" : "s"}. Large files are written in
                      batches, so this can take a while — leaving the page will
                      interrupt the step, not undo what has already been
                      written.
                    </div>
                  )}
                </div>
              )}

              {(stage === "phase2" || stage === "done") && (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="min-w-[16rem] flex-1">
                      <p className="m-0 text-base font-semibold text-gray-900">
                        {stage === "done" ? "Phase 2 complete" : "Phase 2"}
                      </p>
                      <p className="m-0 mt-0.5 text-sm text-gray-500">
                        {stage === "done"
                          ? "Benchmark, Normalize, Summarize, and Category Scores finished. Native KPI briefings are generating in the background for accounts in this upload (keyed by ingest created_at)."
                          : "Benchmark → Normalize → Summarize → Category Scores. Runs straight through."}
                      </p>
                    </div>
                    {fileNameMark}
                  </div>

                  {stage === "phase2" && (
                    <div className="rounded-md border border-gray-200 p-3 text-sm text-gray-600">
                      {busy
                        ? "Phase 2 is running. Steps update in the list below as each one finishes."
                        : "When you continue, scoring runs automatically end to end. Progress appears below."}
                    </div>
                  )}
                </div>
              )}

              {outcomes.length > 0 && (
                <div className="space-y-1 rounded-md border border-gray-200 p-3">
                  {outcomes.map(o => (
                    <div
                      key={o.phase}
                      className="flex items-start gap-2 text-xs"
                    >
                      {o.status === "success" ? (
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />
                      ) : o.status === "failed" ? (
                        <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
                      ) : o.status === "skipped" ? (
                        <MinusCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                      ) : (
                        <span className="mt-0.5 h-3.5 w-3.5" />
                      )}
                      <span className="min-w-[120px] font-medium text-gray-800">
                        {o.phase}
                      </span>
                      <span className="text-gray-600">
                        {o.status}
                        {/* Unit-labelled: Benchmark counts metrics, Summarize counts
                    period rollups, the score phases count accounts. Calling all
                    of them "rows" was wrong for half the pipeline. */}
                        {measureLabel(o) && ` · ${measureLabel(o)}`}
                        {o.error && (
                          <span className="text-red-600">
                            {" "}
                            ·{" "}
                            {customerFacingRunError({
                              phase: o.phase,
                              raw: o.error,
                              variant: "short",
                            })}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {auditWarnings.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <p className="m-0 font-medium">
                    The run is fine, but some steps were not recorded in its
                    history:
                  </p>
                  {/* Keyed by position: two steps can fail with identical text, and
              deduplicating them by key would hide one of the two. */}
                  {auditWarnings.map((w, i) => (
                    <p key={i} className="m-0">
                      {w}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {(() => {
        const footer = (
          <div
            className={
              footerPinned
                ? "flex items-center justify-end gap-2"
                : "mt-3 flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 pt-3"
            }
          >
            <div className="flex justify-end gap-2">
              {stage === "columns" && (
                <Button
                  onClick={() =>
                    persistAndAdvance(
                      "columns",
                      { column_mapping: columns },
                      {
                        outcome_counts: columnCounts,
                      }
                    )
                  }
                  disabled={columnBlockers.length > 0}
                >
                  Continue
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              )}
              {stage === "accounts" && (
                <Button
                  onClick={() =>
                    persistAndAdvance(
                      "accounts",
                      { account_decisions: accountDecisions },
                      {
                        outcome_counts: countDecisions(
                          accountProposals,
                          accountDecisions
                        ),
                      }
                    )
                  }
                  disabled={!refsLoaded}
                >
                  Continue
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              )}
              {stage === "categories" && (
                <Button
                  onClick={() =>
                    persistAndAdvance(
                      "categories",
                      { category_decisions: categoryDecisions },
                      {
                        outcome_counts: countDecisions(
                          categoryProposals,
                          categoryDecisions
                        ),
                      }
                    )
                  }
                  // A category the file contradicts must be chosen before
                  // moving on; importing it under a guess is the bug.
                  disabled={!refsLoaded || unresolvedCategoryConflicts > 0}
                  title={
                    unresolvedCategoryConflicts > 0
                      ? "Choose a category for each row marked needs review"
                      : undefined
                  }
                >
                  Continue
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              )}
              {stage === "metrics" && (
                <Button
                  onClick={() =>
                    persistAndAdvance(
                      "metrics",
                      { metric_decisions: metricDecisions },
                      {
                        outcome_counts: countDecisions(
                          metricProposals,
                          metricDecisions
                        ),
                      }
                    )
                  }
                  disabled={!refsLoaded}
                >
                  Continue
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              )}
              {stage === "import" && (
                <Button
                  onClick={runImport}
                  loading={busy}
                  icon={<Upload className="h-3.5 w-3.5" />}
                >
                  Import
                </Button>
              )}
              {stage === "qc" &&
                (qcSummary ? (
                  <Button
                    onClick={goToHeal}
                    loading={busy}
                    icon={<Wrench className="h-3.5 w-3.5" />}
                  >
                    Continue to Heal
                  </Button>
                ) : (
                  <Button
                    onClick={runQc}
                    loading={busy}
                    icon={<ShieldCheck className="h-3.5 w-3.5" />}
                  >
                    Run QC
                  </Button>
                ))}
              {stage === "heal" && (
                <Button
                  // Unresolved duplicates block the step. Commit holds them back
                  // anyway, so letting the user past here would only hide rows that
                  // silently never reach production.
                  disabled={unresolvedConflicts > 0}
                  title={
                    unresolvedConflicts > 0
                      ? `Resolve ${unresolvedConflicts} duplicate group${
                          unresolvedConflicts === 1 ? "" : "s"
                        } first`
                      : undefined
                  }
                  onClick={async () => {
                    const fixed = Object.keys(healed).length;
                    await dispatch(
                      persistDataHubLedgerStep({
                        runId: run.ledgerRunId,
                        step: "heal",
                        decisions: {},
                        close: {
                          outcome_counts: {
                            rows_reviewed: qcIssues?.length ?? 0,
                            rows_fixed: fixed,
                            conflicts_resolved: conflictsResolved,
                          },
                        },
                      })
                    ).unwrap();
                    await session?.recordStep("heal", {
                      count: fixed,
                      extras: {
                        rows_reviewed: qcIssues?.length ?? 0,
                        rows_fixed: fixed,
                        conflicts_resolved: conflictsResolved,
                      },
                    });
                    tick("heal");
                    goto("promote");
                  }}
                >
                  Continue
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              )}
              {stage === "promote" && (
                <Button
                  onClick={runPromote}
                  loading={busy}
                  icon={<ArrowUpCircle className="h-3.5 w-3.5" />}
                >
                  Promote
                </Button>
              )}
              {stage === "phase2" && (
                <Button onClick={runPhase2} loading={busy}>
                  Run Phase 2
                </Button>
              )}
            </div>
          </div>
        );
        return footerPinned
          ? footerActionsContainer
            ? createPortal(footer, footerActionsContainer)
            : null
          : footer;
      })()}
    </section>
  );
}

/**
 * Coerce the conflicts response to a list.
 *
 * The pane gates Promote, so a non-array — an older backend without the
 * endpoint, a shape change — must degrade to "nothing to adjudicate" rather
 * than throwing and taking the whole console down with it.
 */
function asConflicts(value: unknown): QcConflictGroup[] {
  return Array.isArray(value) ? (value as QcConflictGroup[]) : [];
}

function Loading() {
  return (
    <div className="flex items-center gap-2 py-4 text-sm text-gray-600">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading…
    </div>
  );
}

function Panel({
  title,
  subtitle,
  children,
  headerTrailing,
}: {
  title: string;
  /** Second line under the title (same pattern as Map columns / match steps). */
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  headerTrailing?: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[16rem] flex-1">
          <p className="m-0 text-base font-semibold text-gray-900">{title}</p>
          {subtitle != null && (
            <p className="m-0 mt-0.5 text-sm leading-relaxed text-gray-500">
              {subtitle}
            </p>
          )}
        </div>
        {headerTrailing}
      </div>
      {children != null && (
        <div className="rounded-md border border-gray-200 p-3 text-sm text-gray-600">
          {children}
        </div>
      )}
    </div>
  );
}

export default RunConsole;
